/**
 * SyncPage — driver data the server set aside (OPEN-144 PR 4, DEC-357).
 *
 * Was a "Coming soon" stub that the dashboard's "Open sync" button led to. Now: every parked
 * item in the company (sync_conflicts), newest first, every kind — second run tickets, late
 * field data, and the older kinds — filtered by status (open by default) and kind, and by job
 * when opened from a job's "2nd ticket parked" badge (`?jobId=`). "Load more" walks every
 * page. A row opens the item: the compare with Keep / Use for a second ticket, both sides
 * with Dismiss for anything else. Deciding an item turns the driver's red sync badge back.
 *
 * The list needs REVIEW_TICKET; anyone else is told so. Times local (DEC-004).
 */

import * as React from 'react';
import { useSearchParams } from 'react-router-dom';
import { ApiError } from '@lodex-pro/web-shared';
import { Button } from 'src/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from 'src/components/ui/dialog';

import { formatTimestamp } from '../lib/timestamp';
import { useUserTimezone } from '../lib/use-user-timezone';
import { ParkedDecision } from '../features/sync-conflicts/ParkedDecision';
import { KIND_FILTERS, kindLabel, resolutionLabel } from '../features/sync-conflicts/parked-labels';
import {
  useSyncConflicts,
  type SyncConflictStatusFilter,
} from '../features/sync-conflicts/use-sync-conflicts';

const CELL: React.CSSProperties = {
  padding: '8px 10px',
  borderBottom: '1px solid var(--color-border-secondary)',
  fontSize: '13px',
  textAlign: 'left',
  verticalAlign: 'top',
};
const SELECT: React.CSSProperties = {
  height: '34px',
  borderRadius: '10px',
  border: '1px solid var(--color-border-secondary)',
  backgroundColor: 'var(--color-bg-tertiary)',
  color: 'var(--color-text-primary)',
  padding: '0 8px',
  fontSize: '13px',
};

const STATUS_FILTERS: ReadonlyArray<{ value: SyncConflictStatusFilter; label: string }> = [
  { value: 'OPEN', label: 'Open' },
  { value: 'RESOLVED', label: 'Decided' },
  { value: 'ALL', label: 'All' },
];

export function SyncPage(): React.JSX.Element {
  const tz = useUserTimezone();
  const [searchParams, setSearchParams] = useSearchParams();
  const jobId = searchParams.get('jobId') ?? undefined;
  const [status, setStatus] = React.useState<SyncConflictStatusFilter>('OPEN');
  const [kind, setKind] = React.useState<string>('');
  const [openId, setOpenId] = React.useState<string | null>(null);

  const list = useSyncConflicts({
    status,
    ...(kind !== '' && { kind }),
    ...(jobId !== undefined && { jobId }),
  });
  const items = list.data?.pages.flatMap((p) => p.items) ?? [];
  const forbidden = list.error instanceof ApiError && list.error.status === 403;

  return (
    <div style={{ padding: '24px', maxWidth: '1200px' }}>
      <h2 style={{ fontSize: '20px', fontWeight: 600, marginBottom: '4px' }}>Parked driver data</h2>
      <p style={{ fontSize: '14px', color: 'var(--color-text-secondary)', marginBottom: '16px' }}>
        Data a driver’s phone sent that the server set aside instead of applying: a second run
        ticket on a job that already had one, or a change that reached a job too late. Decide each
        one; the driver’s sync badge clears when theirs are decided.
      </p>

      {forbidden ? (
        <p>You don’t have access to parked driver data. A reviewer or manager can decide these.</p>
      ) : (
        <>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '12px' }}>
            <label style={{ fontSize: '13px' }}>
              Status{' '}
              <select
                aria-label="Status"
                style={SELECT}
                value={status}
                onChange={(e): void => {
                  setStatus(e.target.value as SyncConflictStatusFilter);
                }}
              >
                {STATUS_FILTERS.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </label>
            <label style={{ fontSize: '13px' }}>
              Kind{' '}
              <select
                aria-label="Kind"
                style={SELECT}
                value={kind}
                onChange={(e): void => {
                  setKind(e.target.value);
                }}
              >
                <option value="">All kinds</option>
                {KIND_FILTERS.map((k) => (
                  <option key={k.value} value={k.value}>
                    {k.label}
                  </option>
                ))}
              </select>
            </label>
            {jobId !== undefined && (
              <Button
                variant="outline"
                onClick={(): void => {
                  setSearchParams({}, { replace: true });
                }}
              >
                Showing one job · Show all
              </Button>
            )}
          </div>

          {list.isLoading && <p role="status">Loading…</p>}
          {list.isError && !forbidden && (
            <div style={{ color: 'var(--color-danger)' }}>
              <p>Couldn’t load parked driver data.</p>
              <Button
                variant="outline"
                onClick={(): void => {
                  void list.refetch();
                }}
              >
                Retry
              </Button>
            </div>
          )}
          {!list.isLoading && !list.isError && items.length === 0 && (
            <p style={{ color: 'var(--color-text-secondary)' }}>
              {status === 'OPEN' ? 'Nothing waiting. Every parked item has been decided.' : 'None.'}
            </p>
          )}

          {items.length > 0 && (
            <table style={{ width: '100%', borderCollapse: 'collapse' }} aria-label="Parked items">
              <thead>
                <tr>
                  <th style={CELL}>Ticket</th>
                  <th style={CELL}>Driver</th>
                  <th style={CELL}>Kind</th>
                  <th style={CELL}>Parked</th>
                  <th style={CELL}>Job status</th>
                  <th style={CELL}>Decision</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr key={it.id}>
                    <td style={CELL}>
                      <button
                        type="button"
                        onClick={(): void => {
                          setOpenId(it.id);
                        }}
                        style={{
                          background: 'none',
                          border: 'none',
                          padding: 0,
                          color: 'var(--color-accent-blue)',
                          cursor: 'pointer',
                          fontWeight: 600,
                        }}
                      >
                        {it.jobId === null
                          ? `Shift ${it.shiftStartedAt === null ? '' : formatTimestamp(it.shiftStartedAt, tz)}`.trim()
                          : (it.haulId ?? `Job ${it.jobId.slice(0, 8)}`)}
                      </button>
                    </td>
                    <td style={CELL}>{it.driverName ?? '—'}</td>
                    <td style={CELL}>{kindLabel(it.conflictType)}</td>
                    <td style={CELL}>{formatTimestamp(it.detectedAt, tz)}</td>
                    <td style={CELL}>{(it.jobStatusCode ?? '—').replaceAll('_', ' ')}</td>
                    <td style={CELL}>
                      {it.status === 'OPEN' ? (
                        <span style={{ color: 'var(--color-warning)', fontWeight: 600 }}>
                          {it.conflictType === 'DUPLICATE_CAPTURE'
                            ? 'Keep or use'
                            : it.conflictType === 'SITE_CHANGE'
                              ? 'Apply or dismiss'
                              : 'Dismiss'}
                        </span>
                      ) : (
                        resolutionLabel(it.resolution, it.conflictType)
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {list.hasNextPage && (
            <Button
              variant="outline"
              style={{ marginTop: '12px' }}
              disabled={list.isFetchingNextPage}
              onClick={(): void => {
                void list.fetchNextPage();
              }}
            >
              {list.isFetchingNextPage ? 'Loading…' : 'Load more'}
            </Button>
          )}
        </>
      )}

      <Dialog
        open={openId !== null}
        onOpenChange={(o): void => {
          if (!o) setOpenId(null);
        }}
      >
        <DialogContent style={{ maxWidth: '900px', maxHeight: '90vh', overflow: 'auto' }}>
          <DialogHeader>
            <DialogTitle>Parked item</DialogTitle>
          </DialogHeader>
          {openId !== null && <ParkedDecision id={openId} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
